/**
 * The message composer — captures a user turn and hands it to the store's `send`
 * (which opens the detached run). Disabled while a run is in flight (the store is
 * busy through a `waiting_input` park too, so free text can't jump a pending
 * form). Pure input capture; zero AI logic.
 *
 * While a run streams, the send button becomes STOP. It is the only exit from a
 * long turn that does not cost the user money: the run is detached and server-side,
 * so closing the tab (the previous only option) leaves it generating tokens we pay
 * for and bills the turn in full at the end.
 */
import { Button, Form, TextInput, useT } from '@4f/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import type { CompanionView } from '../../api/types.ts';
import type { ChatStore } from '../../stores/chat.ts';
import { submitTurn } from './composer-model.ts';

export interface ComposerProps {
  readonly store: ChatStore;
  readonly companion: CompanionView;
}

export function Composer(props: ComposerProps): JSX.Element {
  const { t } = useT();
  const [text, setText] = createSignal('');
  const placeholder = (): string =>
    t('chat.composer.placeholder', { companionName: props.companion.name });

  const typed = (): boolean => text().trim() !== '';
  /** A run is live (and therefore stoppable) — busy alone also covers history loads. */
  const stoppable = (): boolean => {
    const status = props.store.stream().status;
    return status === 'streaming' || status === 'waiting_input';
  };

  // SEND only. Enter submits the form, and a stop must never be something you can
  // hit by pressing Enter: typing your next message while she is still replying
  // would kill the run, discard the reply and leave the text unsent.
  //
  // The input is cleared by the POLICY (`submitTurn`), not here, so a turn the
  // store cannot take puts the text back instead of destroying it.
  const submit = (): void => {
    void submitTurn({
      read: text,
      write: (value) => setText(value),
      busy: () => props.store.busy(),
      send: (value) => props.store.send(value),
    });
  };

  return (
    <Form onSubmit={submit} class="f4-composer">
      <TextInput
        class="f4-composer__input"
        value={text()}
        onValue={setText}
        placeholder={placeholder()}
        aria-label={placeholder()}
        autocomplete="off"
      />
      <Show
        when={stoppable()}
        fallback={
          <Button type="submit" variant="primary" disabled={props.store.busy() || !typed()}>
            {t('chat.composer.send')}
          </Button>
        }
      >
        {/* A deliberate `button`, not a submit: only a real click stops the run. */}
        <Button type="button" variant="ghost" onClick={() => void props.store.stop()}>
          {t('chat.composer.stop')}
        </Button>
      </Show>
    </Form>
  );
}
